import type { DefaultFieldValues } from '@kizen/api';
import type { EntityRecordDetail } from '@kizen/api/records';
import {
  isDisplayNameValue,
  isEstimatedCloseDateValue,
  isNameValue,
  isOwnerValue,
  isStageValue,
} from '@kizen/checks/field-value';
import { format } from 'date-fns/format';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import { ListCardCell, ListCardRow } from 'components/Layout/ListCard';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { usePreReleaseFeatures } from '__hooks/usePreReleaseFeatures';
import { StyledListCardItem } from 'pages/ContactsPage/styles';

type OwnerLike = {
  id: string;
  first_name: string;
  last_name: string;
};

type PipelineListItemProps = {
  record: EntityRecordDetail;
  objectId: string;
};

const NO_VALUE = '—';

const OwnerText = ({ value }: { value?: OwnerLike | null }) => {
  const preReleaseFeatures = usePreReleaseFeatures();

  if (!value) {
    return <KizenTypography>{NO_VALUE}</KizenTypography>;
  }

  const formatted = `${value.first_name} ${value.last_name}`;

  if (preReleaseFeatures) {
    return (
      <TextEllipsisWithTooltip
        type="link"
        to={`/team-member/${value.id}/timeline`}
        target="_blank"
      >
        {formatted}
      </TextEllipsisWithTooltip>
    );
  }

  return <TextEllipsisWithTooltip>{formatted}</TextEllipsisWithTooltip>;
};

export const PipelineListItem = ({
  objectId,
  record,
}: PipelineListItemProps) => {
  const { t } = useTranslation();

  const { display_name, estimated_close_date, name, owner, stage } =
    useMemo(() => {
      const result: Pick<
        Partial<DefaultFieldValues>,
        'display_name' | 'estimated_close_date' | 'name' | 'owner' | 'stage'
      > = {};

      for (const fv of Object.values(record.fields)) {
        if (isDisplayNameValue(fv)) {
          result.display_name = fv;
        } else if (isEstimatedCloseDateValue(fv)) {
          result.estimated_close_date = fv;
        } else if (isNameValue(fv)) {
          result.name = fv;
        } else if (isOwnerValue(fv)) {
          result.owner = fv;
        } else if (isStageValue(fv)) {
          result.stage = fv;
        }
      }

      return result;
    }, [record]);

  return (
    <StyledListCardItem>
      <ListCardRow>
        <ListCardCell>
          <TextEllipsisWithTooltip
            type="link"
            to={`${objectId}/${record.id}/details`}
          >
            {display_name?.value || name?.value}
          </TextEllipsisWithTooltip>
        </ListCardCell>
        <ListCardCell>
          <OwnerText value={owner?.value} />
        </ListCardCell>
      </ListCardRow>
      <ListCardRow>
        <ListCardCell>
          <KizenTypography>
            {stage ? stage.value.name : NO_VALUE}
          </KizenTypography>
        </ListCardCell>
        <ListCardCell>
          <KizenTypography>
            {`${t('Close')}: ${
              estimated_close_date?.value
                ? format(estimated_close_date.value, 'MM/dd/yyyy')
                : NO_VALUE
            }`}
          </KizenTypography>
        </ListCardCell>
      </ListCardRow>
    </StyledListCardItem>
  );
};

export default PipelineListItem;
